/**
 * RUN 84 - what the extension push says, and what the page works out before it asks.
 *
 * The confirmation is `03-appointments.md` section A9's push dialogue, word for word where the
 * press allows it: the title, the batch sentence, the list of the matches, the note that the other
 * competition is a separate batch (or, while a match still fails the country rule, the note that
 * says so and a `Cannot Push` that cannot be pressed), and `Confirm Push`. Two things A9 predates
 * are added in sentences of their own, because the dialogue would otherwise promise what the press
 * does not do: which matches are left as they are (already on TMS, or Official), and that
 * `Confirm Push` hands the batch to the TMS Push extension, whose own window is where the post is
 * confirmed (`12-tms-push-extension.md` section 2, principle 2).
 *
 * **Every time the TD reads is on the tournament's clock** (`app/tournamentTime.ts`), and every
 * day is a calendar day read as one (`dayLabel`). The hold is an instant, so it is re-zoned to Tokyo
 * and SAYS it is Tokyo time; it is never printed as the API sends it.
 *
 * Source is ASCII: the dash, the middle dot and the ellipsis the copy uses are written as escapes.
 *
 * **The day push** (`12-tms-push-extension.md` section 6c; the TD's Decision #5 = B, 19 Sep 2026)
 * says, at the end of this file, what `Push Day To TMS` asks and answers: section A9's dialogue once
 * per competition of the day - the existing preview, one section each, Men's first - with the one
 * sentence the day push adds (one Confirm in the extension makes one TMS save per competition, one
 * after the other, and nothing is published), and its answer per competition, in the API's words,
 * with a stop in the extension's.
 */

import { summariseAppointmentPayload, type TmsAppointmentPayload } from "@hockeytd/shared";
import type { TmsCompetitionId } from "@hockeytd/shared";

import { ApiError } from "../../api/client.js";
import type {
  AppointmentFixtureBody,
  AppointmentPushReceiptBody,
  DayPushMatchRefBody,
  ExtensionPushLeftOutBody,
  ExtensionPushReleaseBody,
  ExtensionPushReportBody,
  ExtensionPushResultMessage,
  TmsPushFailureReasonBody,
} from "../../api/types.js";
import { tournamentTimeOfDay } from "../../app/tournamentTime.js";
import { dayLabel } from "../schedule/matchDays.js";
import type {
  RememberedPress,
  TmsDayPushPress,
  TmsPushEnding,
  TmsPushNotTaken,
  TmsPushStep,
} from "./tmsPushFlow.js";
import { TmsPushAnswerUnreadableError, TmsPushPlanMismatchError } from "./tmsPushFlow.js";
import { countOf } from "./vocabulary.js";

export const MDASH = "\u2014";
export const MIDDOT = "\u00b7";
export const ELLIPSIS = "\u2026";

/** `Men's` / `Women's`, from the TMS competition id (1916 men, 1917 women). */
export function competitionWord(competitionId: TmsCompetitionId): string {
  return competitionId === "1917" ? "Women's" : "Men's";
}

/** `Men's Sun 20 Sep` - one batch, named as the dialogue and the notices name it. */
export function batchName(competitionId: TmsCompetitionId, day: string): string {
  return `${competitionWord(competitionId)} ${dayLabel(day)}`;
}

/**
 * `14:32 Tokyo time` - the hold's end on the tournament's clock, and saying which clock. Null for
 * an instant that is not one, so a sentence can leave the time out rather than print a guess.
 */
export function holdClock(holdUntil: string | null): string | null {
  if (holdUntil === null) return null;
  const at = new Date(holdUntil);
  if (Number.isNaN(at.getTime())) return null;
  return `${tournamentTimeOfDay(at)} Tokyo time`;
}

/** `M02` / `M02 and M05` / `M02, M05 and W01`. */
function matchList(matches: readonly { displayMatchNo: string }[]): string {
  const names = matches.map((match) => match.displayMatchNo);
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1] ?? ""}`;
}

/* -- The batch, as the page sees it before it asks ----------------------------------------------- */

/** One competition's matches on the day, sorted into what a push would do with each. */
export interface PushBatchPreview {
  readonly competitionId: TmsCompetitionId;
  readonly day: string;
  /** Owed (not locked) and not Official: what the press would send, in send order. */
  readonly sendable: readonly AppointmentFixtureBody[];
  /** Locked already: the record TMS holds, which a push does not send. */
  readonly onTms: readonly AppointmentFixtureBody[];
  /** Owed but Official in TMS: never sent (section A9a). */
  readonly official: readonly AppointmentFixtureBody[];
  /** Sendable matches with an own-country appointment neither resolved nor overridden. */
  readonly countryFails: readonly AppointmentFixtureBody[];
  /** Every sendable match was reopened for a correction: the press is a re-push. */
  readonly rePush: boolean;
  /** The other competition's matches on the same day - a separate batch. */
  readonly otherCompetition: TmsCompetitionId | null;
  readonly otherCount: number;
}

/** Section A9a's send order, within one competition: kick-off, then match number. */
function bySendOrder(a: AppointmentFixtureBody, b: AppointmentFixtureBody): number {
  if (a.time !== b.time) return a.time < b.time ? -1 : 1;
  return a.matchNo.localeCompare(b.matchNo);
}

/** An own-country appointment that is neither resolved nor overridden (section A7, A9). */
function failsCountryRule(fixture: AppointmentFixtureBody): boolean {
  return fixture.slots.some(
    (slot) =>
      slot.mediaName !== null &&
      slot.status !== "overridden" &&
      (slot.countryConflict || slot.ruleViolation === "own_country"),
  );
}

/**
 * The batch for one competition on the day, from the day as the page holds it. The server decides
 * for itself at the prepare; this is only what the dialogue shows the TD before he asks.
 */
export function pushBatchPreview(
  dayFixtures: readonly AppointmentFixtureBody[],
  competitionId: TmsCompetitionId,
  day: string,
): PushBatchPreview {
  const mine = dayFixtures.filter(
    (fixture) => fixture.date === day && fixture.competitionId === competitionId,
  );
  const owed = mine.filter((fixture) => !fixture.locked);
  const sendable = owed.filter((fixture) => !fixture.frozen).sort(bySendOrder);
  const others = dayFixtures.filter(
    (fixture) => fixture.date === day && fixture.competitionId !== competitionId,
  );
  return {
    competitionId,
    day,
    sendable,
    onTms: mine.filter((fixture) => fixture.locked).sort(bySendOrder),
    official: owed.filter((fixture) => fixture.frozen).sort(bySendOrder),
    countryFails: sendable.filter(failsCountryRule),
    rePush: sendable.length > 0 && sendable.every((fixture) => fixture.needsPush),
    otherCompetition: others[0]?.competitionId ?? null,
    otherCount: others.length,
  };
}

/** The competitions played on the day, Men's first: one push action each. */
export function competitionsOnDay(
  dayFixtures: readonly AppointmentFixtureBody[],
  day: string,
): TmsCompetitionId[] {
  return [
    ...new Set(
      dayFixtures.filter((fixture) => fixture.date === day).map((fixture) => fixture.competitionId),
    ),
  ].sort();
}

/**
 * One match as the dialogue lists it - `Men's M01 | KEN v URU -- TO ... | SJ ... | TJ ...` -
 * through the shared sentence the receipt stores (`summariseAppointmentPayload`), so the list and
 * the receipt cannot describe one crew two ways. An empty slot reads `Unfilled`; the server refuses
 * such a batch, and says which match.
 *
 * Section A9's `{Men's M01}` names the competition on every line, and it is always the same one:
 * the push is pressed from a per-competition button (`Push Men's To TMS`), one press is one
 * competition on one day (12-tms-push-extension.md section 3), so a batch never mixes Men's and
 * Women's - the word is the button's, repeated so a line read alone still says which it is.
 */
export function batchLine(fixture: AppointmentFixtureBody): string {
  const slotOf = (name: "TO" | "SJ" | "TJ") => {
    const slot = fixture.slots.find((entry) => entry.slot === name);
    return {
      tmsPersonId: slot?.tmsPersonId ?? "0",
      mediaName: slot?.mediaName ?? "Unfilled",
      country: slot?.country ?? null,
    };
  };
  const payload: TmsAppointmentPayload = {
    kind: "appointments",
    tmsMatchDbId: fixture.tmsMatchDbId,
    competitionId: fixture.competitionId,
    competitionGender: fixture.competitionGender,
    date: fixture.date,
    displayMatchNo: fixture.displayMatchNo,
    homeTeamCode: fixture.homeTeamCode,
    awayTeamCode: fixture.awayTeamCode,
    slots: { TO: slotOf("TO"), SJ: slotOf("SJ"), TJ: slotOf("TJ") },
  };
  return summariseAppointmentPayload(payload);
}

/* -- The push controls ----------------------------------------------------------------------------- */

/** The extension, as the page last found it. */
export type PushPresenceState = "checking" | "present" | "absent";

/**
 * What the controls say while the TMS Push extension has not answered (frontend review M2). One
 * slow answer - Chrome stops an idle extension worker, and waking it can take more than the
 * second the page waits as it opens - is not "not installed": so it says what to check, the
 * controls stay pressable, and the press asks again (for three seconds), as does the page each
 * time its window regains the focus.
 */
export const EXTENSION_NO_ANSWER_NOTE =
  `The TMS Push extension did not answer ${MDASH} check it is installed, enabled and allowed on this ` +
  "address (https://hockeytd.org exactly, not www), then press again";

export const EXTENSION_CHECKING_REASON = "Looking for the TMS Push extension";

export const WRITE_OUT_REASON = "Unavailable while another write is out";

/**
 * While this tab remembers a press whose result is not recorded yet, no new push starts: a new one
 * would take the old one's place in the tab's memory, and the result it promised to keep with it.
 */
export function unrecordedReason(press: RememberedPress): string {
  return (
    `The last push of ${batchName(press.competitionId, press.day)} is not recorded yet ${MDASH} ` +
    "reload the page to record it first"
  );
}

/** One competition's push control. */
export interface PushActionView {
  readonly competitionId: TmsCompetitionId;
  readonly label: string;
  readonly available: boolean;
  /** This competition's own reason, where the group's does not already say it. */
  readonly reason: string | null;
  /** This competition's push is the write that is out. */
  readonly pushing: boolean;
}

/**
 * Every push control on the day, and the one reason they share where they share one: a write out,
 * a press not yet recorded, or the extension being looked for. A competition with nothing to push
 * has its own reason, on its own control. An extension that did not answer is not a reason - the
 * press asks again - but a NOTE the controls carry, saying what to check (`groupNote`).
 */
export function pushActionViews(
  dayFixtures: readonly AppointmentFixtureBody[],
  day: string,
  presence: PushPresenceState,
  pending: {
    readonly pushing: TmsCompetitionId | null;
    readonly busy: boolean;
    /** A press this tab still remembers whose result is not recorded yet - one press at a time. */
    readonly unrecorded?: RememberedPress | null;
  },
): { readonly actions: readonly PushActionView[]; readonly groupReason: string | null; readonly groupNote: string | null } {
  const unrecorded = pending.unrecorded ?? null;
  const groupReason = pending.busy
    ? WRITE_OUT_REASON
    : unrecorded !== null
      ? unrecordedReason(unrecorded)
      : presence === "checking"
        ? EXTENSION_CHECKING_REASON
        : null;
  const groupNote = groupReason === null && presence === "absent" ? EXTENSION_NO_ANSWER_NOTE : null;
  const actions = competitionsOnDay(dayFixtures, day).map((competitionId): PushActionView => {
    const word = competitionWord(competitionId);
    const pushing = pending.pushing === competitionId;
    const label = pushing ? `Pushing ${word}${ELLIPSIS}` : `Push ${word} To TMS`;
    if (groupReason !== null) {
      return { competitionId, label, available: false, reason: null, pushing };
    }
    const batch = pushBatchPreview(dayFixtures, competitionId, day);
    if (batch.sendable.length > 0) {
      return { competitionId, label, available: true, reason: null, pushing };
    }
    const reason =
      batch.official.length === 0
        ? `Nothing to push: every ${word} match on ${dayLabel(day)} is already locked as the ` +
          "record TMS holds"
        : `Nothing to push: ${matchList(batch.official)} ${
            batch.official.length === 1 ? "is" : "are"
          } Official in TMS, and appointments cannot be changed for an Official match`;
    return { competitionId, label, available: false, reason, pushing };
  });
  return { actions, groupReason, groupNote };
}

/* -- The confirmation (section A9) ------------------------------------------------------------------ */

/** `Pushing Men's -- Sun 20 Sep | 2 Matches Will Be Pushed To TMS As Draft` */
export function pushTitle(batch: PushBatchPreview): string {
  const n = batch.sendable.length;
  return (
    `Pushing ${competitionWord(batch.competitionId)} ${MDASH} ${dayLabel(batch.day)} ${MIDDOT} ` +
    `${String(n)} ${n === 1 ? "Match" : "Matches"} Will Be Pushed To TMS As Draft`
  );
}

/** Section A9's OK: `Confirm Push`, `Confirm Re-Push`, or `Cannot Push` while a match fails. */
export function confirmLabel(batch: PushBatchPreview): string {
  if (batch.countryFails.length > 0) return "Cannot Push";
  return batch.rePush ? "Confirm Re-Push" : "Confirm Push";
}

/**
 * The matches the press leaves as they are, said before it is asked: already locked as the record
 * TMS holds, or Official in TMS. Empty when there are none.
 */
export function leftAsTheyAre(batch: PushBatchPreview): string {
  const parts: string[] = [];
  if (batch.onTms.length > 0) {
    parts.push(
      batch.onTms.length === 1
        ? `${matchList(batch.onTms)} is already locked as the record TMS holds, and is left as it is.`
        : `${matchList(batch.onTms)} are already locked as the record TMS holds, and are left as ` +
            "they are.",
    );
  }
  if (batch.official.length > 0) {
    parts.push(
      batch.official.length === 1
        ? `${matchList(batch.official)} is Official in TMS and will not be pushed: appointments ` +
            "cannot be changed for an Official match."
        : `${matchList(batch.official)} are Official in TMS and will not be pushed: appointments ` +
            "cannot be changed for an Official match.",
    );
  }
  return parts.join(" ");
}

/** Section A9's blocked note, bold half first: `1 match in this batch still fails the country rule.` */
export function blockedLead(batch: PushBatchPreview): string {
  const n = batch.countryFails.length;
  return `${countOf(n, "match")} in this batch still ${n === 1 ? "fails" : "fail"} the country rule.`;
}

export const BLOCKED_TAIL = "Resolve or override it before the batch can be pushed.";

/**
 * Section A9's clean note, first sentence: `The 3 Women's matches on this date are a separate batch
 * and are not affected.` - the count and the competition are the bold part. Null when the other
 * competition plays nothing that day.
 */
export function separateBatch(
  batch: PushBatchPreview,
): { readonly bold: string; readonly rest: string } | null {
  if (batch.otherCompetition === null || batch.otherCount === 0) return null;
  const one = batch.otherCount === 1;
  return {
    bold: `${String(batch.otherCount)} ${competitionWord(batch.otherCompetition)}`,
    rest: one
      ? " match on this date is a separate batch and is not affected."
      : " matches on this date are a separate batch and are not affected.",
  };
}

/** Section A9's clean note, second sentence, verbatim. */
export const UMPIRES_UNTOUCHED = `Umpire slots are left untouched ${MDASH} they belong to the Umpires Managers.`;

/**
 * What the question's own button does now that the post is made in the TD's own browser (section
 * 2), naming the button it shows: `Confirm Push`, or `Confirm Re-Push` for a batch reopened for a
 * correction - and, while a match fails the country rule (the button reads `Cannot Push`), the one
 * it will show once the batch can go.
 */
export function confirmHandsOver(batch: PushBatchPreview): string {
  const label = batch.rePush ? "Confirm Re-Push" : "Confirm Push";
  const lead = batch.countryFails.length > 0 ? `Once the batch can be pushed, ${label}` : label;
  return (
    `${lead} holds this day for up to ten minutes and hands the batch to the TMS Push extension. ` +
    "Nothing reaches TMS until you confirm it again in the extension's own window, and it is saved " +
    `there as draft ${MDASH} publishing stays a step you take in TMS.`
  );
}

/* -- While the press runs ---------------------------------------------------------------------------- */

/** The step the press is at, as the dialogue's live line says it - with the try, while a report is tried again. */
export function stepSentence(step: TmsPushStep, attempt = 1, attempts = 1): string {
  switch (step) {
    case "discovering":
      return `Looking for the TMS Push extension${ELLIPSIS}`;
    case "preparing":
      return `Preparing the push: the server checks every crew and holds the day${ELLIPSIS}`;
    case "handing-over":
      return `Handing the batch to the TMS Push extension${ELLIPSIS}`;
    case "awaiting-confirm":
      return "Waiting for you to confirm in the TMS Push window";
    case "reading":
      return `The TMS Push extension is reading the day in TMS${ELLIPSIS}`;
    case "posting":
      return `The TMS Push extension is posting the day to TMS ${MDASH} one post, never retried${ELLIPSIS}`;
    case "verifying":
      return `The TMS Push extension is reading the day back from TMS to verify it${ELLIPSIS}`;
    case "reporting":
      return attempt <= 1
        ? `Recording what TMS accepted${ELLIPSIS}`
        : `The result is not recorded yet, so it is being sent again, unchanged (try ` +
            `${String(attempt)} of ${String(attempts)})${ELLIPSIS}`;
    case "releasing":
      return (
        `The TMS Push extension did not take this push, so nothing was sent to TMS. Freeing the ` +
        `day${ELLIPSIS}`
      );
    case "recovering":
      return `Asking the TMS Push extension what happened to this push${ELLIPSIS}`;
    case "waiting":
      return (
        "The TMS Push extension is still working on this push. If its window is waiting, " +
        "confirm or cancel it there."
      );
  }
}

/**
 * The extension stops accepting Confirm this long before the hold ends (12 section 6a: clock
 * error, and the time the push's own requests take) - so the sentence says so, and when.
 */
export const CONFIRM_STOPS_BEFORE_HOLD_MS = 120_000;

/**
 * Where the confirmation happens, and until when the day is held - on the tournament's clock -
 * and that the extension's own window stops accepting Confirm two minutes before that. Null until
 * the prepare has answered: before that nothing is held.
 */
export function holdSentence(press: RememberedPress | null): string | null {
  const holdUntil = press?.holdUntil ?? null;
  const clock = holdClock(holdUntil);
  if (clock === null || holdUntil === null) return null;
  const last = holdClock(new Date(Date.parse(holdUntil) - CONFIRM_STOPS_BEFORE_HOLD_MS).toISOString());
  return (
    "The confirmation happens in the TMS Push extension's own window, not on this page. The day " +
    `is held until ${clock}, and the extension stops accepting Confirm two minutes before that` +
    `${last === null ? "" : `, at ${last.replace(" Tokyo time", "")}`}: confirm or cancel there before then.`
  );
}

/* -- Refusals -------------------------------------------------------------------------------------- */

/** Said when no extension answers at the press itself (three seconds): nothing was prepared. */
export const ABSENT_AT_THE_PRESS = `${EXTENSION_NO_ANSWER_NOTE}. Nothing was prepared and nothing was sent to TMS.`;

/** A prepare whose answer never came - or came as a server failure, after which it may have landed. */
const PREPARE_ANSWER_LOST =
  "The Control Centre did not get the server's answer to the push, even asking a second time, so " +
  "it is not known whether the push was prepared. If it was, it holds the day for up to ten " +
  "minutes. Nothing was sent to TMS.";

/**
 * A prepare's refusal, in plain English, by the API's own code. The server's words are added where
 * they name the matches, or which press holds the day and until when - the API writes that time on
 * the tournament's clock (`dayHeldByExtensionMessage`), never as a raw instant.
 */
export function prepareRefusal(error: unknown, competitionId: TmsCompetitionId, day: string): string {
  const where = batchName(competitionId, day);
  const word = competitionWord(competitionId);
  if (error instanceof TmsPushPlanMismatchError) {
    return (
      "The server's plan could not be handed to the TMS Push extension, so nothing was sent to " +
      "TMS. If the server recorded the push, it holds the day for up to ten minutes."
    );
  }
  // No answer the page can read, or a server failure: worded as a LOST answer (and asked twice,
  // `isLostPrepareAnswer`) - the press may have been recorded before it failed.
  if (!(error instanceof ApiError) || error.status >= 500) return PREPARE_ANSWER_LOST;
  switch (error.code) {
    case "push_in_progress":
      return (
        `Another push or lock of ${dayLabel(day)} is out at this moment ${MDASH} a push in the TMS ` +
        "Push extension holds the day until it is reported, or for ten minutes at most " +
        `${MDASH} so nothing was prepared. Wait for it to finish, then look at the day before ` +
        `pushing again. The server said: ${error.message}`
      );
    case "nothing_to_push":
      return (
        `Every ${word} match on ${dayLabel(day)} is already locked as the record TMS holds, so ` +
        "there is nothing to push, and nothing was prepared. To correct a match, unlock it first."
      );
    case "appointments_frozen":
      return (
        `Every ${word} match still owed on ${dayLabel(day)} is Official in TMS, and appointments ` +
        `cannot be changed for an Official match, so nothing was prepared. The server said: ` +
        error.message
      );
    case "appointments_not_ready_to_push":
      return (
        "Not every crew in this batch is ready to push, so nothing was prepared and the day is " +
        "not held. A match is pushed only once its TO, SJ and TJ are appointed, confirmed and clear " +
        `of the country rule. The server named each one: ${error.message}`
      );
    case "actor_not_on_panel":
      return (
        "Your account is not linked to an official on the panel, so a push cannot be recorded " +
        "against your name. Nothing was prepared."
      );
    case "press_id_reused":
      return (
        "This push's press id was already used for another push, so nothing was prepared. Press " +
        "the push again for a fresh one."
      );
    case "press_target_gone":
      return (
        `The day changed while the push was being prepared ${MDASH} a match, or your own record, ` +
        "is no longer there. Nothing was prepared; look at the day again."
      );
    case "press_closed":
      return (
        `The server says this push is already closed ${MDASH} it was reported, or its hold ran out ` +
        `${MDASH} so its plan was not handed out again and nothing was sent to TMS. Press the push ` +
        "again: a new push starts afresh."
      );
    case "push_busy":
      // Not "the day is not held": when this ask was the second of a lost answer (M4), the first may
      // still have reached the server and hold the day (security re-review, item 5).
      return (
        `The server was busy with another change to ${dayLabel(day)} and gave up after five ` +
        "seconds, so this try prepared nothing and nothing was sent to TMS. If an earlier try of " +
        "this push reached the server before its answer was lost, the day is held for up to ten " +
        "minutes. Press the push again in a moment."
      );
    case "not_found":
      return `There is no ${word} match on ${dayLabel(day)} to push. Nothing was prepared.`;
    case "bad_request":
      return `The server refused the request as malformed, so nothing was prepared: ${error.message}`;
    case "forbidden":
      return "Only the Technical Delegate can push. Nothing was prepared.";
    case "unauthorised":
      return "Your sign-in is no longer valid. Sign in again; nothing was prepared.";
    case "network_error":
      return PREPARE_ANSWER_LOST;
    default:
      return `The push of ${where} was not prepared, and nothing was sent to TMS: ${error.message}`;
  }
}

/**
 * The prepare refusals that mean the day moved on under the page - so the day is read again, and
 * the controls stop offering a press the server will only refuse again.
 */
export function isMovedOnPushRefusal(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    (error.code === "nothing_to_push" ||
      error.code === "appointments_frozen" ||
      error.code === "appointments_not_ready_to_push" ||
      error.code === "press_target_gone" ||
      error.code === "not_found")
  );
}

/**
 * A report's refusal, in plain English. `retryable`: the press is still remembered, and the same
 * result may be sent again - the report is resumable, so a second send writes only what is missing.
 * `inQuestion`: said inside the question, beside `Report Again`; once the question is closed, it
 * says what the page will do the next time it opens.
 */
export function reportRefusal(
  error: unknown,
  press: RememberedPress,
  retryable: boolean,
  inQuestion = true,
): string {
  const where = batchName(press.competitionId, press.day);
  const kept = inQuestion
    ? " The result the TMS Push extension sent is kept: reload this page to try recording it " +
      "again, or press Report Again. Sent again, it records only what is still missing."
    : " The result the TMS Push extension sent is kept: this page records it the next time it " +
      `opens ${MDASH} reload it to try again now.`;
  const again = retryable ? kept : "";
  if (error instanceof TmsPushAnswerUnreadableError) {
    return (
      "The server answered the report, but not with this push's answer, so what it recorded is " +
      `not known yet.${again}`
    );
  }
  if (!(error instanceof ApiError)) {
    return `The result of the push of ${where} was not recorded.${again}`;
  }
  switch (error.code) {
    case "bad_request":
      return (
        "The server refused the TMS Push extension's result as malformed, so nothing was recorded: " +
        `${error.message} The extension may have posted to TMS ${MDASH} check ${where} in TMS.`
      );
    case "not_the_presser":
      return (
        "This push was pressed by another official, and only they can report it. Nothing was " +
        `recorded ${MDASH} check ${where} in TMS.`
      );
    case "not_found":
      return `The server has no record of this push, so its result could not be recorded. Check ${where} in TMS.`;
    case "press_id_reused":
      return (
        "The server does not hold this press as a TMS Push extension press, so its result could " +
        `not be recorded. Check ${where} in TMS.`
      );
    case "actor_not_on_panel":
      // The press is forgotten here, after a post that may have landed (security re-review, item 5).
      return (
        "Your account is not linked to an official on the panel, so nothing was recorded. The " +
        `extension may have posted to TMS ${MDASH} check ${where} in TMS.`
      );
    case "push_in_progress":
      return `The result of this push is being recorded at this moment.${again}`;
    case "push_completed_view_failed":
      return `The result was recorded, but the day could not be read back to show you.${again}`;
    case "network_error":
      return `The Control Centre could not reach the API, so the result may not be recorded yet.${again}`;
    case "unauthorised":
      return `Your sign-in is no longer valid, so the result was not recorded. Sign in again.${again}`;
    default: {
      const said =
        error.status >= 500 ? "The server could not record the result just now." : `The result was not recorded: ${error.message}`;
      // Kept, it can be recorded later; forgotten, the post it may have made is for the TD to check.
      return again === "" ? `${said} The extension may have posted to TMS ${MDASH} check ${where} in TMS.` : `${said}${again}`;
    }
  }
}

/* -- The answer -------------------------------------------------------------------------------------- */

/** Every match the press owed, as the answer lists it. */
function plannedCount(answer: ExtensionPushReportBody): number {
  return answer.matches.length + answer.notAttempted.length;
}

/**
 * Whether a `partial` answer's only shortfall is Official matches (frontend review L1): every
 * match the press sent was accepted AND locked, nothing went unrecorded, and the API calls it
 * partial only because it also names matches that are Official in TMS - which no push could send.
 */
function officialOnlyShortfall(answer: ExtensionPushReportBody): boolean {
  return (
    answer.outcome === "partial" &&
    answer.matches.length > 0 &&
    answer.matches.every((match) => match.sent && match.locked) &&
    answer.notAttempted.length === 0 &&
    answer.refusedOfficial.length > 0
  );
}

/** The dialogue's title once the press has an answer: `Pushed | Men's Sun 20 Sep`. */
export function answerTitle(
  answer: ExtensionPushReportBody,
  result: ExtensionPushResultMessage,
  press: RememberedPress,
): string {
  const where = batchName(press.competitionId, press.day);
  if (result.cancelled) return `Push Cancelled ${MIDDOT} ${where}`;
  if (officialOnlyShortfall(answer)) return `Pushed ${MIDDOT} ${where}`;
  switch (answer.outcome) {
    case "sent":
      return `Pushed ${MIDDOT} ${where}`;
    case "partial":
      return `Partly Pushed ${MIDDOT} ${where}`;
    case "failed":
      return `Not Pushed ${MIDDOT} ${where}`;
  }
}

/**
 * The answer in one sentence - section A9's success toast, verbatim, when every match was sent:
 * `Pushed | Men's Sun 20 Sep -- 2 matches sent to TMS as draft.` The notice repeats it once
 * the dialogue is closed.
 */
export function answerHeadline(
  answer: ExtensionPushReportBody,
  result: ExtensionPushResultMessage,
  press: RememberedPress,
): string {
  const where = batchName(press.competitionId, press.day);
  if (result.cancelled) {
    return (
      `Push cancelled ${MIDDOT} ${where} ${MDASH} cancelled in the TMS Push extension. Nothing was ` +
      "sent to TMS, and the day is free again."
    );
  }
  const accepted = answer.matches.filter((match) => match.sent);
  if (officialOnlyShortfall(answer)) {
    const official = answer.refusedOfficial;
    return (
      `Pushed ${MIDDOT} ${where} ${MDASH} ${countOf(accepted.length, "match")} sent to TMS as draft; ` +
      `${matchList(official)} ${official.length === 1 ? "is" : "are"} Official in TMS, so ` +
      `${official.length === 1 ? "it was" : "they were"} not pushed.`
    );
  }
  switch (answer.outcome) {
    case "sent":
      return `Pushed ${MIDDOT} ${where} ${MDASH} ${countOf(accepted.length, "match")} sent to TMS as draft.`;
    case "partial": {
      const unlocked = accepted.filter((match) => match.sent && !match.locked).length;
      return (
        `Partly pushed ${MIDDOT} ${where} ${MDASH} ${String(accepted.length)} of ` +
        `${countOf(plannedCount(answer), "match")} accepted by TMS as draft` +
        (unlocked === 0 ? "" : `, ${String(unlocked)} of them not locked here`) +
        "."
      );
    }
    case "failed":
      return (
        `Not pushed ${MIDDOT} ${where} ${MDASH} no match was recorded as accepted by TMS, and ` +
        "nothing was locked." +
        (result.postsMade === 1
          ? ` The TMS Push extension did make its one post, so check ${where} in TMS before pushing again.`
          : "")
      );
  }
}

/**
 * The collateral warnings to show: the report's answer carries them (contract change, 18 Sep 2026);
 * where it carries none - an answer replayed from the receipts, which do not keep them - the
 * extension's own result still has them.
 */
export function answerCollateral(
  answer: ExtensionPushReportBody | null,
  result: ExtensionPushResultMessage,
): readonly string[] {
  const fromAnswer = answer?.collateral ?? [];
  return fromAnswer.length > 0 ? fromAnswer : result.collateral;
}

/** What the extension did to TMS, in its own count. */
export function postsSentence(result: ExtensionPushResultMessage, press: RememberedPress): string {
  if (result.postsMade === 0) return "The TMS Push extension made no post to TMS.";
  return (
    `The TMS Push extension made one post to TMS for ${batchName(press.competitionId, press.day)}, ` +
    "and read the day back to check it."
  );
}

/** A replayed answer says that it is one. Null otherwise. */
export function replayedSentence(answer: ExtensionPushReportBody): string | null {
  return answer.replayed
    ? "This is the answer already recorded for this push: nothing new was written."
    : null;
}

const REASON_WORDS: Readonly<Record<TmsPushFailureReasonBody, string>> = {
  transport_unavailable: "Not sent",
  session_expired: `Not accepted ${MIDDOT} TMS sign-in needed`,
  unreachable: `Not accepted ${MIDDOT} TMS unreachable`,
  form_changed: `Not accepted ${MIDDOT} the TMS form changed`,
  rejected_by_tms: "Not accepted",
};

/** One line of the answer: a match, what became of it in words, and whose words say why. */
export interface AnswerLine {
  readonly key: string;
  readonly match: string;
  /** In words, never colour alone: `Accepted as draft | Locked here`, `Not accepted`, ... */
  readonly verdict: string;
  readonly tone: "good" | "warn" | "bad" | "quiet";
  /** The extension's own words for this match, verbatim; null where it gave none. */
  readonly extensionSaid: string | null;
  /** What the server recorded, where it is not the extension's words. */
  readonly recorded: string | null;
}

/**
 * The answer, match by match: each receipt with its outcome and whether it locked, the matches with
 * no receipt, the Official matches refused and the ones already on TMS - with the extension's own
 * words beside each, and the server's where it recorded something else.
 */
export function answerLines(
  answer: ExtensionPushReportBody,
  result: ExtensionPushResultMessage,
): AnswerLine[] {
  const said = (tmsMatchDbId: number): string | null => {
    const entry = result.results.find((candidate) => candidate.tmsMatchDbId === tmsMatchDbId);
    return entry === undefined || entry.detail.trim() === "" ? null : entry.detail;
  };
  const lines: AnswerLine[] = [];
  for (const match of answer.matches) {
    const extensionSaid = said(match.tmsMatchDbId);
    const recorded = match.receipt.detail === extensionSaid ? null : match.receipt.detail;
    if (match.sent) {
      // Why an accepted match was not locked is the server's to say, where it says it - on the match
      // (a report after the hold had run out) or in its receipt - and is shown as written; only where
      // it says nothing does the page give the general reason.
      const serverSaid =
        typeof match.detail === "string" && match.detail.trim() !== "" ? match.detail : null;
      lines.push({
        key: `m-${String(match.tmsMatchDbId)}`,
        match: match.displayMatchNo,
        verdict: match.locked
          ? `Accepted as draft ${MIDDOT} Locked here, On TMS`
          : serverSaid !== null || recorded !== null
            ? `Accepted as draft ${MIDDOT} Not locked here`
            : `Accepted as draft ${MIDDOT} Not locked here: its crew changed, or could not be ` +
              `locked, while the push was out. TMS holds what was sent ${MDASH} check the match, then ` +
              "Confirm Day and Mark Day Locked.",
        tone: match.locked ? "good" : "warn",
        extensionSaid,
        recorded: serverSaid ?? recorded,
      });
    } else {
      lines.push({
        key: `m-${String(match.tmsMatchDbId)}`,
        match: match.displayMatchNo,
        verdict: REASON_WORDS[match.reason] ?? "Not accepted",
        tone: "bad",
        extensionSaid,
        recorded,
      });
    }
  }
  for (const missing of answer.notAttempted) {
    lines.push({
      key: `n-${String(missing.tmsMatchDbId)}`,
      match: missing.displayMatchNo,
      verdict: "No receipt",
      tone: "bad",
      extensionSaid: said(missing.tmsMatchDbId),
      recorded: missing.detail,
    });
  }
  for (const refused of answer.refusedOfficial) {
    lines.push(refLine("o", refused, "Official in TMS \u00b7 not pushed: appointments cannot be changed for an Official match"));
  }
  for (const done of answer.alreadyOnTms) {
    lines.push(refLine("t", done, "Already on TMS \u00b7 locked before this push, and left as it is"));
  }
  return lines;
}

function refLine(prefix: string, ref: DayPushMatchRefBody, verdict: string): AnswerLine {
  return {
    key: `${prefix}-${String(ref.tmsMatchDbId)}`,
    match: ref.displayMatchNo,
    verdict,
    tone: "quiet",
    extensionSaid: null,
    recorded: null,
  };
}

/** The lead of the collateral warning, which is shown whenever the extension reported any. */
export const COLLATERAL_LEAD =
  "The TMS Push extension reported changes on the TMS day page beyond this push. They are not " +
  `stored here ${MDASH} check each one in TMS now:`;

/* -- No result ------------------------------------------------------------------------------------- */

/** The dialogue's title when the press ended with no result to report - or one already recorded. */
export function noResultTitle(press: RememberedPress, recorded: readonly AppointmentPushReceiptBody[] | null = null): string {
  const where = batchName(press.competitionId, press.day);
  return recorded !== null && recorded.length > 0
    ? `Push Already Recorded ${MIDDOT} ${where}`
    : `No Result From The TMS Push Extension ${MIDDOT} ${where}`;
}

/**
 * The API already holds this press's receipts (frontend review L9): it was reported by another tab,
 * or before this page reloaded. Said match by match, from the receipts - not "no result".
 *
 * Fix round 2 (R3): a press of a day push that this page ITSELF failed to handle (`failed`) is not said
 * to have been found empty in the extension - the page may never have asked, or the result it was
 * reporting is still there, never acknowledged - nor who reported it: only that the page failed, and
 * what the Control Centre holds.
 */
function recordedText(
  why: Extract<TmsPushEnding, { kind: "no-result" }>["why"],
  press: RememberedPress,
  recorded: readonly AppointmentPushReceiptBody[],
): string {
  const where = batchName(press.competitionId, press.day);
  const lines = recorded.map((receipt) =>
    receipt.outcome === "accepted_draft"
      ? `${receipt.displayMatchNo} accepted by TMS as draft${receipt.locked ? " and locked" : ", not locked here"}`
      : `${receipt.displayMatchNo} not accepted`,
  );
  const listed = lines.length <= 1 ? (lines[0] ?? "") : `${lines.slice(0, -1).join(", ")} and ${lines[lines.length - 1] ?? ""}`;
  if (why === "failed") {
    return (
      `This page failed while it was handling the push of ${where}, but the Control Centre has ` +
      `already recorded it: ${listed}. Look at the day here before pushing again.`
    );
  }
  return (
    `The TMS Push extension holds no result for the push of ${where} any more, but the Control ` +
    `Centre has already recorded it: ${listed}. It was reported by another tab, or before this ` +
    `page reloaded ${MDASH} look at the day here before pushing again.`
  );
}

/**
 * What the page knows when it has no result to report, and what to do - on the tournament's clock.
 * `now` is the device's clock, which the extension shares; the hold's end is the server's instant.
 */
export function noResultText(
  why: Extract<TmsPushEnding, { kind: "no-result" }>["why"],
  press: RememberedPress,
  lapsed: boolean,
  recorded: readonly AppointmentPushReceiptBody[] | null = null,
): string {
  if (recorded !== null && recorded.length > 0) return recordedText(why, press, recorded);
  const where = batchName(press.competitionId, press.day);
  const clock = holdClock(press.holdUntil);
  const check = `check ${where} in TMS before pushing again.`;
  const free =
    clock === null
      ? "If the server recorded the push, it holds the day for up to ten minutes from when you " +
        "pressed Confirm Push."
      : lapsed
        ? `The hold on the day lapsed at ${clock}, so the day is free again.`
        : `The day stays held until ${clock}, then it is free again.`;
  switch (why) {
    case "unknown":
      if (press.holdUntil === null) {
        return (
          "This tab was reloaded while the push was being prepared, so the TMS Push extension never " +
          `received it and nothing was sent to TMS. ${free}`
        );
      }
      return lapsed
        ? `The TMS Push extension holds no result for the push of ${where}. The push may not have ` +
            `happened ${MDASH} ${check} ${free}`
        : `The TMS Push extension holds no result for the push of ${where}: it may never have ` +
            `reached the extension, or its result was lost ${MDASH} ${check} ${free}`;
    case "unreadable":
      return (
        "The TMS Push extension answered in a form this page cannot read, so nothing was recorded " +
        `here. It may have posted to TMS ${MDASH} ${check} ${free}`
      );
    case "no-answer":
      return (
        "The TMS Push extension did not answer, so this page cannot find out what happened to the " +
        `push of ${where}. The push may not have happened ${MDASH} ${check} ${free}` +
        (lapsed ? "" : " This page asks the extension again the next time it opens.")
      );
    case "lapsed":
      return (
        `The hold on the day ended at ${clock ?? "its time"} before the TMS Push extension reported, ` +
        "and the extension posts nothing once its hold has lapsed. If its window is still open, " +
        `cancel it there. The push may not have happened ${MDASH} ${check} The day is free again.`
      );
    case "failed":
      // Fix round 1 (day push review, P10): the page itself failed while handling this push. Said
      // as that - never as the extension's silence - and the push is still asked about next time.
      return (
        `This page failed while it was handling the push of ${where}, so what became of it is not ` +
        `known here. The push may or may not have happened ${MDASH} ${check} ${free} This page asks ` +
        "the TMS Push extension about it again the next time it opens."
      );
  }
}

/* -- A plan the extension never took ------------------------------------------------------------- */

/** Why the plan never reached the extension, in plain English - with its own words, where it gave them. */
export function notTakenSentence(notTaken: TmsPushNotTaken): string {
  if (notTaken.by === "page") {
    // Fix round 1 (P9): the page went away before it handed the push over.
    if (notTaken.reason === "gone") {
      return "This page was closed before the push was handed to the TMS Push extension, so it never received it.";
    }
    // Day push (section 6c): a `push-day` message the page never sent, for its size.
    if (notTaken.reason === "too-large") {
      return (
        "This day push was too large to hand to the TMS Push extension in one message (more than " +
        `1 MiB), so it was never sent to it ${MDASH} push each competition with its own push, and ` +
        "tell the build team."
      );
    }
    return notTaken.reason === "busy"
      ? "This page was still waiting on another push, so this one was never handed to the TMS Push extension."
      : // It answered at the press a moment before, so "not installed" would be a guess (M2): say
        // what to check instead, as the controls' own note does.
        "The TMS Push extension could not be reached when the push was handed over, so it never " +
          `received it ${MDASH} check it is installed, enabled and allowed on this address ` +
          "(https://hockeytd.org exactly, not www), then press again.";
  }
  switch (notTaken.reason) {
    case "busy":
      return (
        "The TMS Push extension is already running another push, or waiting for its Confirm, so " +
        "it did not take this one: finish or cancel that one in its own window first. The " +
        `extension said: ${notTaken.detail}`
      );
    case "invalid-message":
      return (
        "The TMS Push extension refused this push's plan as malformed, so it did not take it " +
        `${MDASH} tell the build team. The extension said: ${notTaken.detail}`
      );
  }
}

/** The question's title when the extension did not take the plan. */
export function notTakenTitle(press: RememberedPress, released: boolean): string {
  return `${released ? "Push Not Taken, Day Released" : "Push Not Taken"} ${MIDDOT} ${batchName(press.competitionId, press.day)}`;
}

/**
 * What a recorded release did, after `The push ... was released`. A single press is the day's one
 * push, so its release frees the day. A press of a DAY push shares the day with the day push's other
 * presses - the day stays held while any of them is out - so its release frees only its own hold:
 * `the day is free again` would be false while another press of it is still out, and is never said
 * of one (fix round 2, R5, from the API review).
 */
function releasedClause(ofDayPush: boolean): string {
  return ofDayPush
    ? "every match in it is recorded as not sent, and it no longer holds the day"
    : "every match in it is recorded as not sent, and the day is free again";
}

/**
 * The plan never reached the extension, and the press was released. `ofDayPush`: a press of a day
 * push, named - its competition's section, and the notice that joins every section's words - and
 * never said to have freed the day ({@link releasedClause}).
 */
export function releasedText(notTaken: TmsPushNotTaken, press: RememberedPress, ofDayPush = false): string {
  const released = ofDayPush
    ? `The push of ${batchName(press.competitionId, press.day)} was released`
    : "The push was released";
  return `${notTakenSentence(notTaken)} Nothing was sent to TMS. ${released}: ${releasedClause(ofDayPush)}.`;
}

/**
 * The plan never reached the extension, and the release was not recorded - or, answered 500 after it
 * was, not read back. `ofDayPush`: a press of a day push, whose release never frees the day on its
 * own ({@link releasedClause}) - so what a refusal says was not released is the press's own HOLD,
 * never "the day" (final round, M2). What it says of the hold stands for a day push too: every press
 * of one shares its one `hold_until` (section 6c), so at that instant the day push holds nothing, and
 * the day is free again. The single press's words are exactly as they were.
 */
export function releaseRefusal(
  error: unknown,
  notTaken: TmsPushNotTaken,
  press: RememberedPress,
  ofDayPush = false,
): string {
  const where = batchName(press.competitionId, press.day);
  const base = `${notTakenSentence(notTaken)} Nothing was sent to TMS.`;
  const check = `Check ${where} in TMS before pushing again.`;
  const clock = holdClock(press.holdUntil);
  const held =
    clock === null
      ? "The day stays held for up to ten minutes from when you pressed Confirm Push, then it is free again."
      : `The day stays held until ${clock} at the latest, then it is free again.`;
  // What a release would have freed: the day, for the single press - the day's one push; for a press
  // of a day push, its own hold.
  const freed = ofDayPush ? "its hold" : "the day";
  if (error instanceof TmsPushAnswerUnreadableError) {
    return (
      `${base} The server answered the release, but not with this push's answer, so whether ` +
      `${ofDayPush ? "its hold was released" : "the day was freed"} is not known. ${held}`
    );
  }
  if (!(error instanceof ApiError)) {
    return `${base} ${ofDayPush ? "Its release was not recorded." : "The release of the day was not recorded."} ${held}`;
  }
  switch (error.code) {
    case "press_closed":
      return (
        `${base} The server did not release it: the push already has a receipt, or its hold has run ` +
        `out, so the day is not held by it. ${check}`
      );
    case "push_completed_view_failed":
      // Answered 500 AFTER the release was recorded: it was released; only the day's view failed.
      return (
        `${base} ${ofDayPush ? `The push of ${where}` : "The push"} was released ${MDASH} ` +
        `${releasedClause(ofDayPush)} ${MDASH} but the day could not be read back to show you. Look ` +
        "at the day again before pushing."
      );
    case "push_in_progress":
      return `${base} The server was recording this push at that very moment, so it was not released from here. ${held}`;
    case "network_error":
      return `${base} The Control Centre could not reach the API to release ${freed}. ${held}`;
    case "not_the_presser":
      return `${base} This push was pressed by another official, and only they can release it. ${held}`;
    case "actor_not_on_panel":
      return `${base} Your account is not linked to an official on the panel, so ${freed} was not released. ${held} ${check}`;
    default:
      return `${base} The server did not release ${freed}: ${error.message} ${held} ${check}`;
  }
}

/** A released press's matches, as the release recorded them - every one "not sent". */
export function releasedLines(answer: ExtensionPushReleaseBody): AnswerLine[] {
  return answer.matches.map((match) => ({
    key: `m-${String(match.tmsMatchDbId)}`,
    match: match.displayMatchNo,
    verdict: match.sent ? `Accepted as draft ${MIDDOT} check it in TMS` : (REASON_WORDS[match.reason] ?? "Not accepted"),
    tone: match.sent ? "warn" : "quiet",
    extensionSaid: null,
    recorded: match.receipt.detail,
  }));
}

/* -- The day push (section 6c; Decision #5 = B, 19 Sep 2026) ------------------------------------ */

/**
 * The control's own label: section 6c's `Push Day to TMS`, in the head's title case, as `Push Men's
 * To TMS` beside it is written (the orchestrator's ruling, fix round 1).
 */
export const PUSH_DAY_LABEL = "Push Day To TMS";

/** The day's push control. */
export interface PushDayActionView {
  readonly label: string;
  readonly available: boolean;
  /** Its own reason, where the group's does not already say it. */
  readonly reason: string | null;
  /** The day push is the write that is out. */
  readonly pushing: boolean;
}

/**
 * `Push Day To TMS`, for the day the Match Day dropdown names - or null on a day with no match, where
 * the per-competition controls are not drawn either. It shares the group's reason (a write out, a
 * press not yet recorded, the extension being looked for); otherwise it can be pressed while ANY
 * competition of the day has a match to send - the server leaves out one with nothing owed - and says
 * why not when none has.
 */
export function pushDayActionView(
  dayFixtures: readonly AppointmentFixtureBody[],
  day: string,
  pending: { readonly pushingDay: boolean; readonly groupReason: string | null },
): PushDayActionView | null {
  const competitions = competitionsOnDay(dayFixtures, day);
  if (competitions.length === 0) return null;
  const pushing = pending.pushingDay;
  const label = pushing ? `Pushing Day${ELLIPSIS}` : PUSH_DAY_LABEL;
  if (pending.groupReason !== null) return { label, available: false, reason: null, pushing };
  const anything = competitions.some(
    (competitionId) => pushBatchPreview(dayFixtures, competitionId, day).sendable.length > 0,
  );
  if (anything) return { label, available: true, reason: null, pushing };
  return {
    label,
    available: false,
    reason:
      `Nothing to push: every match on ${dayLabel(day)} is already locked as the record TMS holds, ` +
      "or Official in TMS",
    pushing,
  };
}

/** The day push as the page sees it before it asks: every competition of the day, Men's first. */
export interface PushDayPreview {
  readonly day: string;
  readonly batches: readonly PushBatchPreview[];
}

export function pushDayPreview(dayFixtures: readonly AppointmentFixtureBody[], day: string): PushDayPreview {
  return {
    day,
    batches: competitionsOnDay(dayFixtures, day).map((competitionId) =>
      pushBatchPreview(dayFixtures, competitionId, day),
    ),
  };
}

/** The batches that send something - the competitions the day push will push, in the send order. */
function goingBatches(preview: PushDayPreview): PushBatchPreview[] {
  return preview.batches.filter((batch) => batch.sendable.length > 0);
}

/**
 * A batch whose every owed match is Official in TMS: the server refuses the WHOLE day push for it
 * (`appointments_frozen`, section 6c, all or nothing), so the question says so before it is asked.
 */
function refusedWhole(batch: PushBatchPreview): boolean {
  return batch.sendable.length === 0 && batch.official.length > 0;
}

/**
 * Whether the day cannot be pushed as it stands: a match that still fails the country rule (section
 * A9's `Cannot Push`), or a competition whose owed matches are all Official, which the server would
 * refuse the whole day push for.
 */
export function pushDayBlocked(preview: PushDayPreview): boolean {
  return preview.batches.some((batch) => batch.countryFails.length > 0 || refusedWhole(batch));
}

/** `Pushing The Day -- Sun 20 Sep | 6 Matches Will Be Pushed To TMS As Draft` */
export function pushDayTitle(preview: PushDayPreview): string {
  const n = preview.batches.reduce((sum, batch) => sum + batch.sendable.length, 0);
  return (
    `Pushing The Day ${MDASH} ${dayLabel(preview.day)} ${MIDDOT} ` +
    `${String(n)} ${n === 1 ? "Match" : "Matches"} Will Be Pushed To TMS As Draft`
  );
}

/** Section A9's OK, for the day: `Confirm Push`, `Confirm Re-Push`, or `Cannot Push`. */
export function pushDayConfirmLabel(preview: PushDayPreview): string {
  if (pushDayBlocked(preview)) return "Cannot Push";
  const going = goingBatches(preview);
  return going.length > 0 && going.every((batch) => batch.rePush) ? "Confirm Re-Push" : "Confirm Push";
}

/** One competition's section heading: `Men's -- 4 Matches`, or `Women's -- Nothing To Push`. */
export function pushDaySectionTitle(batch: PushBatchPreview): string {
  const n = batch.sendable.length;
  return n === 0
    ? `${competitionWord(batch.competitionId)} ${MDASH} Nothing To Push`
    : `${competitionWord(batch.competitionId)} ${MDASH} ${String(n)} ${n === 1 ? "Match" : "Matches"}`;
}

/**
 * What a competition with nothing to send says in its section, or null when it sends something: left
 * out of the day push (every match of it already locked), or - every owed match Official - the reason
 * the whole day push cannot go.
 */
export function pushDaySectionNote(batch: PushBatchPreview): string | null {
  if (batch.sendable.length > 0) return null;
  const word = competitionWord(batch.competitionId);
  if (refusedWhole(batch)) {
    return (
      `Every ${word} match still owed on ${dayLabel(batch.day)} is Official in TMS, and appointments ` +
      "cannot be changed for an Official match, so the server refuses the whole day push while it " +
      "stands. Push the other competition with its own push instead."
    );
  }
  return (
    `Every ${word} match on ${dayLabel(batch.day)} is already locked as the record TMS holds, so ` +
    `there is nothing of ${word} to push, and it is left out.`
  );
}

/** `Men's` / `Men's, then Women's` - the competitions in the order they are saved. */
function thenList(words: readonly string[]): string {
  if (words.length <= 1) return words[0] ?? "";
  return `${words.slice(0, -1).join(", ")}, then ${words[words.length - 1] ?? ""}`;
}

/**
 * Section 6c's sentence: what the question's own button does, and that one Confirm in the extension
 * makes one TMS save per competition, one after the other, and that nothing is published - naming the
 * button it shows (or, while the day cannot go, the one it will show).
 */
export function pushDayHandsOver(preview: PushDayPreview): string {
  const going = goingBatches(preview).map((batch) => competitionWord(batch.competitionId));
  const label = going.length > 0 && goingBatches(preview).every((batch) => batch.rePush) ? "Confirm Re-Push" : "Confirm Push";
  const lead = pushDayBlocked(preview) ? `Once the day can be pushed, ${label}` : label;
  const saves =
    going.length > 1
      ? `One Confirm in the TMS Push extension's own window then makes one TMS save per competition, ` +
        `one after the other: ${thenList(going)}.`
      : "One Confirm in the TMS Push extension's own window then makes its one TMS save.";
  return (
    `${lead} holds this day for up to ten minutes and hands the day's batches to the TMS Push ` +
    `extension. ${saves} Nothing is published ${MDASH} the saves are drafts, and publishing stays a ` +
    "step you take in TMS."
  );
}

/**
 * The step the day push is at, as the question's live line says it. The day's own steps say the day;
 * a press's stage says its competition first - except the wait for Confirm, which is the whole day
 * push's one Confirm.
 */
export function dayStepSentence(
  step: TmsPushStep,
  press: RememberedPress | null,
  attempt = 1,
  attempts = 1,
): string {
  switch (step) {
    case "discovering":
      return stepSentence(step);
    case "preparing":
      return (
        "Preparing the day push: the server checks every crew of every competition and holds the " +
        `day${ELLIPSIS}`
      );
    case "handing-over":
      return `Handing the day's batches to the TMS Push extension${ELLIPSIS}`;
    case "awaiting-confirm":
      return (
        `Waiting for you to confirm in the TMS Push window ${MDASH} one Confirm sends every ` +
        "competition of the day, one after the other"
      );
    default:
      return press === null
        ? stepSentence(step, attempt, attempts)
        : `${competitionWord(press.competitionId)}: ${stepSentence(step, attempt, attempts)}`;
  }
}

/** A day push's prepare, asked twice and never answered - or answered as a server failure. */
const DAY_PREPARE_ANSWER_LOST =
  "The Control Centre did not get the server's answer to the day push, even asking a second time, " +
  "so it is not known whether it was prepared. If it was, it holds the day for up to ten minutes. " +
  "Nothing was sent to TMS.";

/**
 * A day push's prepare refusal, in plain English, by the API's own code. One competition's refusal
 * refuses the whole day push (section 6c, all or nothing): it is said as that competition's refusal -
 * the single press's own words for it - with the fact that nothing of either competition went.
 */
export function dayPrepareRefusal(error: unknown, day: string): string {
  if (error instanceof TmsPushPlanMismatchError) {
    return (
      "The server's plans could not be handed to the TMS Push extension, so nothing was sent to TMS. " +
      "If the server recorded the day push, it holds the day for up to ten minutes."
    );
  }
  if (!(error instanceof ApiError) || error.status >= 500) return DAY_PREPARE_ANSWER_LOST;
  if (error.competitionId !== null) {
    return (
      `The day push goes whole or not at all, and ${competitionWord(error.competitionId)} was ` +
      "refused, so nothing of either competition was prepared and nothing was sent to TMS. " +
      `${prepareRefusal(error, error.competitionId, day)} To push the other competition now, use ` +
      "its own push."
    );
  }
  switch (error.code) {
    case "push_in_progress":
      return (
        `Another push or lock of ${dayLabel(day)} is out at this moment ${MDASH} a push in the TMS ` +
        "Push extension holds the day until it is reported, or for ten minutes at most " +
        `${MDASH} so nothing was prepared. Wait for it to finish, then look at the day before ` +
        `pushing again. The server said: ${error.message}`
      );
    case "nothing_to_push":
      return (
        `Every match on ${dayLabel(day)} is already locked as the record TMS holds, so there is ` +
        "nothing to push, and nothing was prepared. To correct a match, unlock it first."
      );
    case "not_found":
      return `There is no match on ${dayLabel(day)} to push. Nothing was prepared.`;
    case "actor_not_on_panel":
      return (
        "Your account is not linked to an official on the panel, so a push cannot be recorded " +
        "against your name. Nothing was prepared."
      );
    case "push_busy":
      return (
        `The server was busy with another change to ${dayLabel(day)} and gave up after five ` +
        "seconds, so this try prepared nothing and nothing was sent to TMS. If an earlier try of " +
        "this day push reached the server before its answer was lost, the day is held for up to ten " +
        "minutes. Press the day push again in a moment."
      );
    case "bad_request":
      return `The server refused the request as malformed, so nothing was prepared: ${error.message}`;
    case "forbidden":
      return "Only the Technical Delegate can push. Nothing was prepared.";
    case "unauthorised":
      return "Your sign-in is no longer valid. Sign in again; nothing was prepared.";
    case "network_error":
      return DAY_PREPARE_ANSWER_LOST;
    default:
      return `The day push of ${dayLabel(day)} was not prepared, and nothing was sent to TMS: ${error.message}`;
  }
}

/**
 * Where a press of a day push ended with no post and every match carrying the same words - the day
 * push STOPPED before it (section 6c's stop rule: "Not sent: this day push stopped because the save
 * for Men's did not read back as planned..."), its window closed before its turn, or its hold ran out
 * before its post - those words, once, exactly as the extension wrote them. Null otherwise.
 */
export function dayStopWords(result: ExtensionPushResultMessage): string | null {
  if (result.cancelled || result.postsMade !== 0) return null;
  const first = result.results[0];
  if (first === undefined) return null;
  return result.results.every((entry) => entry.outcome === "failed" && entry.detail === first.detail)
    ? first.detail
    : null;
}

/**
 * A press of a day push that this page ITSELF failed to handle, with nothing recorded for it (fix
 * round 2, R3): said as that - the Control Centre page failed while handling it, and nothing was
 * reported for it - never as `No Result From The TMS Push Extension`, whose silence it never was. One
 * the API already holds receipts for is `Push Already Recorded`, as any other.
 */
function failedUnrecorded(ending: Extract<TmsDayPushPress["ending"], { kind: "no-result" }>): boolean {
  return ending.why === "failed" && (ending.recorded === null || ending.recorded.length === 0);
}

/**
 * A press of a day push: its section's heading, in the single press's own words for its ending - but
 * for two that are the day push's own (fix round 2): a release frees the press's own HOLD, never the
 * day, which another press of the day push may still hold (R5); and the page's own failure is headed
 * as that (R3).
 */
export function dayPressTitle(entry: TmsDayPushPress): string {
  const ending = entry.ending;
  const where = batchName(entry.press.competitionId, entry.press.day);
  switch (ending.kind) {
    case "reported":
      return answerTitle(ending.answer, ending.result, ending.press);
    case "report-refused":
      return `Result Not Recorded ${MDASH} ${where}`;
    case "released":
      return `Push Not Taken, Hold Released ${MIDDOT} ${where}`;
    case "release-refused":
      return notTakenTitle(ending.press, false);
    case "no-result":
      return failedUnrecorded(ending)
        ? `Control Centre Page Failed, Nothing Reported ${MIDDOT} ${where}`
        : noResultTitle(ending.press, ending.recorded);
  }
}

/**
 * A press of a day push in one sentence: the answer's headline, or what became of it - in the same
 * day push's own words as its heading (fix round 2, R3 and R5).
 */
export function dayPressVerdict(entry: TmsDayPushPress): string {
  const ending = entry.ending;
  const where = batchName(entry.press.competitionId, entry.press.day);
  switch (ending.kind) {
    case "reported":
      return answerHeadline(ending.answer, ending.result, ending.press);
    case "report-refused":
      return `Result not recorded ${MIDDOT} ${where} ${MDASH} the TMS Push extension's result is kept.`;
    case "released":
      return `Push not taken, hold released ${MIDDOT} ${where} ${MDASH} nothing was sent to TMS.`;
    case "release-refused":
      return `Push not taken ${MIDDOT} ${where} ${MDASH} nothing was sent to TMS.`;
    case "no-result":
      if (failedUnrecorded(ending)) {
        return `Nothing reported ${MIDDOT} ${where} ${MDASH} the Control Centre page failed while handling it; check it in TMS.`;
      }
      return ending.recorded !== null && ending.recorded.length > 0
        ? `Push already recorded ${MIDDOT} ${where}.`
        : `No result from the TMS Push extension ${MIDDOT} ${where} ${MDASH} check it in TMS.`;
  }
}

/**
 * A press of a day push in full, where its ending is not a report: the single press's own sentence for
 * it - a release said as the press's own, never as the day freed (fix round 2, R5). `lapsed`: its hold
 * had run out when the day push ended. `inQuestion`: said inside the question, beside `Report Again`;
 * once it is closed, what the page does the next time it opens.
 */
export function dayPressText(entry: TmsDayPushPress, lapsed: boolean, inQuestion = true): string {
  const ending = entry.ending;
  switch (ending.kind) {
    case "reported":
      return answerHeadline(ending.answer, ending.result, ending.press);
    case "report-refused":
      return reportRefusal(ending.error, ending.press, ending.retryable, inQuestion);
    case "released":
      return releasedText(ending.notTaken, ending.press, true);
    case "release-refused":
      return releaseRefusal(ending.error, ending.notTaken, ending.press, true);
    case "no-result":
      return noResultText(ending.why, ending.press, lapsed, ending.recorded);
  }
}

/** A competition the server left out of the day push, and why (section 6c, `leftOut`). */
export function leftOutSentence(entry: ExtensionPushLeftOutBody, day: string): string {
  const word = competitionWord(entry.competitionId);
  switch (entry.reason) {
    case "nothing_to_push":
      return (
        `${word} was not in this push: every ${word} match on ${dayLabel(day)} is already locked as ` +
        "the record TMS holds, so there was nothing of it to push."
      );
    case "no_match_on_day":
      return `${word} was not in this push: it plays no match on ${dayLabel(day)}.`;
    case "not_in_this_push":
      return (
        `${word} was not in this push: it was left out when this day push was prepared, and a day ` +
        "push never takes a competition in afterwards."
      );
  }
}

/** Whether a reported press was pushed whole - `sent`, or partial only for its Official matches. */
function pushedWhole(answer: ExtensionPushReportBody): boolean {
  return answer.outcome === "sent" || officialOnlyShortfall(answer);
}

/**
 * Fix round 1 (P2) - what a RECOVERED day push's answer says of the presses it does not list: the tab
 * remembered only the ones it had not yet seen recorded.
 */
export const RECOVERED_NOT_LISTED =
  "Competitions of this day push recorded before the page reloaded are not listed here; their rows " +
  "show what was recorded.";

/**
 * The day push's title once it has an answer: `Day Pushed | Sun 20 Sep`, and so on. A RECOVERED day
 * push (fix round 1, P2) never has a whole-day title - it answers only for the presses the tab still
 * remembered - and is `Push Recovered | Sun 20 Sep`.
 */
export function dayAnswerTitle(presses: readonly TmsDayPushPress[], day: string, recovered = false): string {
  const where = dayLabel(day);
  if (recovered) return `Push Recovered ${MIDDOT} ${where}`;
  if (presses.length > 0 && presses.every(({ ending }) => ending.kind === "reported" && ending.result.cancelled)) {
    return `Day Push Cancelled ${MIDDOT} ${where}`;
  }
  if (presses.length > 0 && presses.every(({ ending }) => ending.kind === "reported" && !ending.result.cancelled && pushedWhole(ending.answer))) {
    return `Day Pushed ${MIDDOT} ${where}`;
  }
  const accepted = presses.some(
    ({ ending }) => ending.kind === "reported" && ending.answer.matches.some((match) => match.sent),
  );
  return accepted ? `Day Partly Pushed ${MIDDOT} ${where}` : `Day Not Pushed ${MIDDOT} ${where}`;
}

/**
 * The day push's answer in a paragraph: each competition's verdict, in the send order - the answer's
 * headline, in the API's words - then each competition left out. The question's own opening sentence.
 */
export function dayAnswerSummary(
  presses: readonly TmsDayPushPress[],
  leftOut: readonly ExtensionPushLeftOutBody[],
  day: string,
  recovered = false,
): string {
  return [
    ...presses.map(dayPressVerdict),
    ...leftOut.map((entry) => leftOutSentence(entry, day)),
    ...(recovered ? [RECOVERED_NOT_LISTED] : []),
  ].join(" ");
}

/**
 * What the day push leaves on the page once its question is closed: each competition's own sentence
 * in full - the answer's headline, or what became of it and what to do - and the competitions left out.
 */
export function dayAnswerNotice(
  presses: readonly TmsDayPushPress[],
  leftOut: readonly ExtensionPushLeftOutBody[],
  day: string,
  lapsed: readonly string[],
  recovered = false,
): string {
  return [
    ...presses.map((entry) => dayPressText(entry, lapsed.includes(entry.press.pressId), false)),
    ...leftOut.map((entry) => leftOutSentence(entry, day)),
    ...(recovered ? [RECOVERED_NOT_LISTED] : []),
  ].join(" ");
}

/**
 * Whether the day push's notice is a failure: nothing of any competition was accepted by TMS, and it
 * was not simply cancelled in the extension - which is the TD's own choice, not a failure.
 */
export function dayAnswerIsFailure(presses: readonly TmsDayPushPress[]): boolean {
  const accepted = presses.some(
    ({ ending }) => ending.kind === "reported" && ending.answer.matches.some((match) => match.sent),
  );
  const cancelled =
    presses.length > 0 && presses.every(({ ending }) => ending.kind === "reported" && ending.result.cancelled);
  return !accepted && !cancelled;
}
